/**
 * ICRC账户地址处理工具
 *
 * 该模块提供ICRC-1标准的账户地址编码和解码功能：
 * 1. 支持Principal格式转换为ICRC账户格式
 * 2. 支持子账户地址编码（principal-checksum.subaccount）
 * 3. 支持地址解码和验证
 * 4. 提供地址格式化显示功能
 *
 * 格式说明：
 * - 默认账户（无子账户）：xxxxx-xxxxx-xxxxx-xxxxx-xxx
 * - 带子账户：xxxxx-xxxxx-xxxxx-xxxxx-xxx-checksum.subaccount
 *
 * 示例：
 * - unn7l-aqaaa-aaaau-ab7ka-cai （无子账户）
 * - unn7l-aqaaa-aaaau-ab7ka-cai-7pixohy.1 （子账户1）
 */

import { Principal } from '@dfinity/principal';

// CRC32查找表 - 用于计算校验和
const CRC32_TABLE = new Uint32Array([
  0x00000000, 0x77073096, 0xee0e612c, 0x990951ba, 0x076dc419, 0x706af48f,
  0xe963a535, 0x9e6495a3, 0x0edb8832, 0x79dcb8a4, 0xe0d5e91e, 0x97d2d988,
  0x09b64c2b, 0x7eb17cbd, 0xe7b82d07, 0x90bf1d91, 0x1db71064, 0x6ab020f2,
  0xf3b97148, 0x84be41de, 0x1adad47d, 0x6ddde4eb, 0xf4d4b551, 0x83d385c7,
  0x136c9856, 0x646ba8c0, 0xfd62f97a, 0x8a65c9ec, 0x14015c4f, 0x63066cd9,
  0xfa0f3d63, 0x8d080df5, 0x3b6e20c8, 0x4c69105e, 0xd56041e4, 0xa2677172,
  0x3c03e4d1, 0x4b04d447, 0xd20d85fd, 0xa50ab56b, 0x35b5a8fa, 0x42b2986c,
  0xdbbbc9d6, 0xacbcf940, 0x32d86ce3, 0x45df5c75, 0xdcd60dcf, 0xabd13d59,
  0x26d930ac, 0x51de003a, 0xc8d75180, 0xbfd06116, 0x21b4f4b5, 0x56b3c423,
  0xcfba9599, 0xb8bda50f, 0x2802b89e, 0x5f058808, 0xc60cd9b2, 0xb10be924,
  0x2f6f7c87, 0x58684c11, 0xc1611dab, 0xb6662d3d, 0x76dc4190, 0x01db7106,
  0x98d220bc, 0xefd5102a, 0x71b18589, 0x06b6b51f, 0x9fbfe4a5, 0xe8b8d433,
  0x7807c9a2, 0x0f00f934, 0x9609a88e, 0xe10e9818, 0x7f6a0dbb, 0x086d3d2d,
  0x91646c97, 0xe6635c01, 0x6b6b51f4, 0x1c6c6162, 0x856530d8, 0xf262004e,
  0x6c0695ed, 0x1b01a57b, 0x8208f4c1, 0xf50fc457, 0x65b0d9c6, 0x12b7e950,
  0x8bbeb8ea, 0xfcb9887c, 0x62dd1ddf, 0x15da2d49, 0x8cd37cf3, 0xfbd44c65,
  0x4db26158, 0x3ab551ce, 0xa3bc0074, 0xd4bb30e2, 0x4adfa541, 0x3dd895d7,
  0xa4d1c46d, 0xd3d6f4fb, 0x4369e96a, 0x346ed9fc, 0xad678846, 0xda60b8d0,
  0x44042d73, 0x33031de5, 0xaa0a4c5f, 0xdd0d7cc9, 0x5005713c, 0x270241aa,
  0xbe0b1010, 0xc90c2086, 0x5768b525, 0x206f85b3, 0xb966d409, 0xce61e49f,
  0x5edef90e, 0x29d9c998, 0xb0d09822, 0xc7d7a8b4, 0x59b33d17, 0x2eb40d81,
  0xb7bd5c3b, 0xc0ba6cad, 0xedb88320, 0x9abfb3b6, 0x03b6e20c, 0x74b1d29a,
  0xead54739, 0x9dd277af, 0x04db2615, 0x73dc1683, 0xe3630b12, 0x94643b84,
  0x0d6d6a3e, 0x7a6a5aa8, 0xe40ecf0b, 0x9309ff9d, 0x0a00ae27, 0x7d079eb1,
  0xf00f9344, 0x8708a3d2, 0x1e01f268, 0x6906c2fe, 0xf762575d, 0x806567cb,
  0x196c3671, 0x6e6b06e7, 0xfed41b76, 0x89d32be0, 0x10da7a5a, 0x67dd4acc,
  0xf9b9df6f, 0x8ebeeff9, 0x17b7be43, 0x60b08ed5, 0xd6d6a3e8, 0xa1d1937e,
  0x38d8c2c4, 0x4fdff252, 0xd1bb67f1, 0xa6bc5767, 0x3fb506dd, 0x48b2364b,
  0xd80d2bda, 0xaf0a1b4c, 0x36034af6, 0x41047a60, 0xdf60efc3, 0xa867df55,
  0x316e8eef, 0x4669be79, 0xcb61b38c, 0xbc66831a, 0x256fd2a0, 0x5268e236,
  0xcc0c7795, 0xbb0b4703, 0x220216b9, 0x5505262f, 0xc5ba3bbe, 0xb2bd0b28,
  0x2bb45a92, 0x5cb36a04, 0xc2d7ffa7, 0xb5d0cf31, 0x2cd99e8b, 0x5bdeae1d,
  0x9b64c2b0, 0xec63f226, 0x756aa39c, 0x026d930a, 0x9c0906a9, 0xeb0e363f,
  0x72076785, 0x05005713, 0x95bf4a82, 0xe2b87a14, 0x7bb12bae, 0x0cb61b38,
  0x92d28e9b, 0xe5d5be0d, 0x7cdcefb7, 0x0bdbdf21, 0x86d3d2d4, 0xf1d4e242,
  0x68ddb3f8, 0x1fda836e, 0x81be16cd, 0xf6b9265b, 0x6fb077e1, 0x18b74777,
  0x88085ae6, 0xff0f6a70, 0x66063bca, 0x11010b5c, 0x8f659eff, 0xf862ae69,
  0x616bffd3, 0x166ccf45, 0xa00ae278, 0xd70dd2ee, 0x4e048354, 0x3903b3c2,
  0xa7672661, 0xd06016f7, 0x4969474d, 0x3e6e77db, 0xaed16a4a, 0xd9d65adc,
  0x40df0b66, 0x37d83bf0, 0xa9bcae53, 0xdebb9ec5, 0x47b2cf7f, 0x30b5ffe9,
  0xbdbdf21c, 0xcabac28a, 0x53b39330, 0x24b4a3a6, 0xbad03605, 0xcdd70693,
  0x54de5729, 0x23d967bf, 0xb3667a2e, 0xc4614ab8, 0x5d681b02, 0x2a6f2b94,
  0xb40bbe37, 0xc30c8ea1, 0x5a05df1b, 0x2d02ef8d
]);

// Base32字母表 - 用于校验和编码
const BASE32_ALPHABET = [
  "a", "b", "c", "d", "e", "f", "g", "h", "i", "j",
  "k", "l", "m", "n", "o", "p", "q", "r", "s", "t",
  "u", "v", "w", "x", "y", "z", "2", "3", "4", "5", "6", "7",
];

/**
 * 账户信息接口
 */
export interface ICRCAccount {
  owner: string;  // Principal格式的所有者地址
  subaccount?: string;  // 十六进制格式的子账户（可选）
}

/**
 * 地址解析结果
 */
export interface ParsedAddress {
  isValid: boolean;
  isSubaccount: boolean;  // 是否包含子账户
  owner?: string;  // Principal格式的所有者
  subaccount?: string;  // 十六进制格式的子账户
  formatted?: string;  // 格式化后的显示地址
  error?: string;  // 错误信息
}

/**
 * 计算CRC32校验和
 */
function calculateCrc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;

  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i];
    crc = CRC32_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * 将Principal文本转换为字节数组
 */
function principalToBytes(principal: string): Uint8Array | null {
  try {
    // 使用 @dfinity/principal 库解析 Principal
    const principalObj = Principal.fromText(principal.trim());
    return principalObj.toUint8Array();
  } catch (error) {
    console.error('Invalid principal:', principal, error);
    return null;
  }
}

/**
 * 计算ICRC校验和
 */
function calculateChecksum(owner: string, subaccountHex: string): string {
  const ownerBytes = principalToBytes(owner);
  if (!ownerBytes) {
    throw new Error('Invalid principal');
  }

  // 将子账户十六进制转换为32字节数组（左填充）
  const subaccount = new Uint8Array(32);
  const hexBytes = [];

  // 确保十六进制字符串是偶数长度
  let normalizedHex = subaccountHex;
  if (normalizedHex.length % 2 !== 0) {
    normalizedHex = '0' + normalizedHex;
  }

  // 转换十六进制为字节
  for (let i = 0; i < normalizedHex.length; i += 2) {
    const byteHex = normalizedHex.slice(i, i + 2);
    const value = parseInt(byteHex, 16);
    if (isNaN(value)) {
      throw new Error(`Invalid hex: ${byteHex}`);
    }
    hexBytes.push(value);
  }

  // 左填充到32字节
  const startIndex = 32 - hexBytes.length;
  for (let i = 0; i < hexBytes.length; i++) {
    subaccount[startIndex + i] = hexBytes[i];
  }

  // 连接owner和subaccount字节
  const combined = new Uint8Array(ownerBytes.length + 32);
  combined.set(ownerBytes, 0);
  combined.set(subaccount, ownerBytes.length);

  // 计算CRC32
  const crc = calculateCrc32(combined);

  // 转换为base32编码（7个字符）
  const d = (shift: number) => BASE32_ALPHABET[(crc >>> shift) & 0x1f];
  return d(27) + d(22) + d(17) + d(12) + d(7) + d(2) + BASE32_ALPHABET[(crc & 0x03) << 3];
}

/**
 * 编码ICRC账户地址
 *
 * @param account 账户信息
 * @returns 编码后的地址字符串
 *
 * 示例：
 * - {owner: "unn7l-aqaaa-aaaau-ab7ka-cai"} => "unn7l-aqaaa-aaaau-ab7ka-cai"
 * - {owner: "unn7l-aqaaa-aaaau-ab7ka-cai", subaccount: "1"} => "unn7l-aqaaa-aaaau-ab7ka-cai-7pixohy.1"
 */
export function encodeIcrcAccount(account: ICRCAccount): string {
  const { owner, subaccount } = account;

  // 如果没有子账户或子账户为0，直接返回owner
  if (!subaccount || subaccount === '0' || subaccount === '00') {
    return owner;
  }

  // 移除子账户前导0（但保留单个0）
  const trimmedSubaccount = subaccount.replace(/^0+/, '') || '0';

  // 如果子账户全为0，返回owner
  if (trimmedSubaccount === '0') {
    return owner;
  }

  try {
    // 计算校验和
    const checksum = calculateChecksum(owner, trimmedSubaccount);

    // 返回格式化地址
    return `${owner}-${checksum}.${trimmedSubaccount}`;
  } catch (error) {
    console.error('编码ICRC账户失败:', error);
    // 如果编码失败，返回原始格式
    return `${owner}:0x${subaccount.padStart(64, '0')}`;
  }
}

/**
 * 解析ICRC账户地址
 *
 * @param address 地址字符串
 * @returns 解析结果
 *
 * 支持格式：
 * 1. Principal格式：xxxxx-xxxxx-xxxxx-xxxxx-xxx
 * 2. ICRC格式：xxxxx-xxxxx-xxxxx-xxxxx-xxx-checksum.subaccount
 * 3. 兼容格式：xxxxx-xxxxx-xxxxx-xxxxx-xxx:0x000...
 */
export function parseIcrcAccount(address: string): ParsedAddress {
  if (!address || typeof address !== 'string') {
    return {
      isValid: false,
      isSubaccount: false,
      error: 'Address must be a non-empty string'
    };
  }

  const trimmed = address.trim();

  // 处理兼容格式 owner:0x...
  if (trimmed.includes(':0x')) {
    const [owner, subaccountWithPrefix] = trimmed.split(':0x');
    const subaccountHex = subaccountWithPrefix || '';

    // 检查子账户是否全为0
    const isDefaultSubaccount = !subaccountHex || /^0+$/.test(subaccountHex);

    if (isDefaultSubaccount) {
      return {
        isValid: true,
        isSubaccount: false,
        owner,
        formatted: owner
      };
    }

    // 移除前导0
    const trimmedSubaccount = subaccountHex.replace(/^0+/, '') || '0';

    // 尝试编码为ICRC格式
    const encoded = encodeIcrcAccount({
      owner,
      subaccount: trimmedSubaccount
    });

    return {
      isValid: true,
      isSubaccount: true,
      owner,
      subaccount: trimmedSubaccount,
      formatted: encoded
    };
  }

  // 处理ICRC格式 owner-checksum.subaccount
  if (trimmed.includes('.')) {
    const dotIndex = trimmed.indexOf('.');
    const lastDashIndex = trimmed.lastIndexOf('-', dotIndex);

    if (lastDashIndex === -1) {
      return {
        isValid: false,
        isSubaccount: false,
        error: '无效的ICRC地址格式'
      };
    }

    const owner = trimmed.slice(0, lastDashIndex);
    const checksum = trimmed.slice(lastDashIndex + 1, dotIndex);
    const subaccountHex = trimmed.slice(dotIndex + 1);

    // 验证校验和长度
    if (checksum.length !== 7) {
      return {
        isValid: false,
        isSubaccount: false,
        error: '校验和长度错误'
      };
    }

    // 验证子账户十六进制
    if (!/^[0-9a-fA-F]+$/.test(subaccountHex)) {
      return {
        isValid: false,
        isSubaccount: false,
        error: '子账户必须是十六进制'
      };
    }

    // 验证校验和
    try {
      const expectedChecksum = calculateChecksum(owner, subaccountHex);
      if (expectedChecksum !== checksum) {
        console.warn('校验和不匹配:', { expected: expectedChecksum, actual: checksum });
        // 暂时不强制校验和验证，因为可能有不同的实现
      }
    } catch (error) {
      console.warn('校验和验证失败:', error);
    }

    return {
      isValid: true,
      isSubaccount: true,
      owner,
      subaccount: subaccountHex,
      formatted: trimmed
    };
  }

  // 处理纯Principal格式
  if (trimmed.includes('-')) {
    return {
      isValid: true,
      isSubaccount: false,
      owner: trimmed,
      formatted: trimmed
    };
  }

  return {
    isValid: false,
    isSubaccount: false,
    error: '无效的地址格式'
  };
}

/**
 * 格式化账户对象为显示字符串
 *
 * @param account 账户对象（来自API响应）
 * @returns 格式化后的地址字符串
 *
 * 处理API返回的各种账户格式：
 * 1. 字符串格式：直接返回
 * 2. 对象格式：{owner: string, subaccount?: string | number[] | null}
 */
export function formatAccountToString(account: any): string {
  // 如果是字符串，直接处理
  if (typeof account === 'string') {
    // 检查是否已经是ICRC格式
    const parsed = parseIcrcAccount(account);
    return parsed.formatted || account;
  }

  // 如果不是对象或没有owner，返回原值
  if (!account || typeof account !== 'object' || !account.owner) {
    return String(account || '');
  }

  const { owner, subaccount } = account;

  // 处理各种子账户格式
  let subaccountHex: string | undefined;

  if (subaccount) {
    if (typeof subaccount === 'string') {
      // 字符串格式，可能是十六进制
      if (subaccount.startsWith('0x')) {
        subaccountHex = subaccount.slice(2);
      } else {
        subaccountHex = subaccount;
      }
    } else if (Array.isArray(subaccount)) {
      // 数组格式，转换为十六进制
      const hasNonZero = subaccount.some(byte => byte !== 0);
      if (hasNonZero) {
        subaccountHex = subaccount
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join('');
      }
    } else if (typeof subaccount === 'object' && subaccount.data) {
      // 可能是Uint8Array或类似对象
      const data = Array.from(subaccount.data || subaccount) as number[];
      const hasNonZero = data.some(byte => byte !== 0);
      if (hasNonZero) {
        subaccountHex = data
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join('');
      }
    }
  }

  // 如果有子账户，编码为ICRC格式
  if (subaccountHex) {
    // 移除前导0
    const trimmed = subaccountHex.replace(/^0+/, '') || '0';

    // 如果不是默认子账户，编码
    if (trimmed !== '0') {
      return encodeIcrcAccount({
        owner,
        subaccount: trimmed
      });
    }
  }

  // 默认返回owner
  return owner;
}

/**
 * 将地址转换为API参数格式
 *
 * @param address 用户输入的地址
 * @returns API URL路径和参数
 */
export function addressToApiFormat(address: string): string {
  const parsed = parseIcrcAccount(address);

  if (!parsed.isValid || !parsed.owner) {
    // 如果解析失败，返回原始地址
    return address;
  }

  if (parsed.isSubaccount && parsed.subaccount) {
    // 确保子账户是64位十六进制（左填充0）
    const paddedSubaccount = parsed.subaccount.padStart(64, '0');
    // 返回 owner:0x... 格式，这是后端API期望的格式
    return `${parsed.owner}:0x${paddedSubaccount}`;
  }

  // 没有子账户，直接返回owner
  return parsed.owner;
}

/**
 * 将地址转换为API参数格式（分离owner和subaccount）
 *
 * @param address 用户输入的地址
 * @returns API参数对象 {owner: string, subaccount?: string}
 */
export function addressToApiParams(address: string): { owner: string; subaccount?: string } | null {
  const parsed = parseIcrcAccount(address);

  if (!parsed.isValid || !parsed.owner) {
    return null;
  }

  if (parsed.isSubaccount && parsed.subaccount) {
    // 确保子账户是64位十六进制（左填充0）
    const paddedSubaccount = parsed.subaccount.padStart(64, '0');
    return {
      owner: parsed.owner,
      subaccount: paddedSubaccount
    };
  }

  return { owner: parsed.owner };
}

/**
 * 格式化地址用于显示（截断）
 *
 * @param address 完整地址
 * @param startLength 开始保留的字符数
 * @param endLength 结尾保留的字符数
 * @returns 截断后的地址
 */
export function formatAddressDisplay(address: string, startLength = 12, endLength = 10): string {
  if (!address) return '';

  // 先解析并格式化地址
  const parsed = parseIcrcAccount(address);
  const formatted = parsed.formatted || address;

  // 如果地址较短，不需要截断
  if (formatted.length <= startLength + endLength + 3) {
    return formatted;
  }

  // 截断显示
  const start = formatted.slice(0, startLength);
  const end = formatted.slice(-endLength);
  return `${start}...${end}`;
}

/**
 * 验证地址格式是否有效
 *
 * @param address 地址字符串
 * @returns 是否有效
 */
export function isValidIcrcAddress(address: string): boolean {
  const parsed = parseIcrcAccount(address);
  return parsed.isValid;
}

/**
 * 获取地址的所有者Principal
 *
 * @param address 地址字符串
 * @returns Principal字符串或null
 */
export function getAddressOwner(address: string): string | null {
  const parsed = parseIcrcAccount(address);
  return parsed.owner || null;
}

/**
 * 检查是否是子账户地址
 *
 * @param address 地址字符串
 * @returns 是否包含子账户
 */
export function hasSubaccount(address: string): boolean {
  const parsed = parseIcrcAccount(address);
  return parsed.isSubaccount;
}